import React, { useMemo } from 'react';

import OptionList from '@repo/editor/src/components/OptionList/OptionList';
import Popover from '@repo/editor/src/components/Popover/Popover';
import {
  userStoreSelector,
  useUserStore,
} from '@repo/editor/src/stores/userStore';
import { withEventScope } from '@repo/event-processor';
import { IconButton } from '@repo/ui/components/Buttons';
import { circledIconTheme } from '@repo/ui/components/Icon';
import CreateIcon from '@repo/ui/components/Icon/CreateIcon';
import DeleteIcon from '@repo/ui/components/Icon/DeleteIcon';
import DesignElementSettingsIcon from '@repo/ui/components/Icon/DesignElementSettingsIcon';
import ElementBookmarkIcon from '@repo/ui/components/Icon/ElementBookmarkIcon';
import ElementDeleteIcon from '@repo/ui/components/Icon/ElementDeleteIcon';
import ElementEditIcon from '@repo/ui/components/Icon/ElementEditIcon';
import FilledElementBookmarkIcon from '@repo/ui/components/Icon/FilledElementBookmarkIcon';
import FilledHeartIcon from '@repo/ui/components/Icon/FilledHeartIcon';
import HeartIcon from '@repo/ui/components/Icon/HeartIcon';
import InfoIcon from '@repo/ui/components/Icon/InfoIcon';
import InfoThickIcon from '@repo/ui/components/Icon/InfoThickIcon';
import LikeIcon from '@repo/ui/components/Icon/LikeIcon';
import LinkIcon from '@repo/ui/components/Icon/LinkIcon';
import LinkShareIcon from '@repo/ui/components/Icon/LinkShareIcon';
import { fireToast } from '@repo/ui/components/Toast';

import { TopActionsProps } from 'components/PostPreview/types';
import { useShowLikeIcon } from 'global/growthbookUtils';
import { buildPostShareUrl, buildUpdateTemplateUrl } from 'global/urlUtils';
import { postsStoreSelector, usePostsStore } from 'stores/postsStore';
import { FlexRow } from 'styles/layouts';
import { LayoutSwitch } from 'styles/queries';

import { DATA_TEST_ID_PREFIX } from './config';
import { useBookmarks } from './utils/useBookmarks';

const TopActions: React.FC<TopActionsProps> = ({
  isOptionList,
  post,
  canEdit,
  canLike,
  onDetailsClick,
  onDelete,
  liked,
  onLike,
}) => {
  const likePost = usePostsStore(postsStoreSelector.likePost);
  const { isBookmarked, toggleBookmark } = useBookmarks();
  const user = useUserStore(userStoreSelector.user);
  const isBookmarkIconEnabled = useShowLikeIcon();

  const actions = useMemo(() => {
    const actionsArray = [];

    if (canEdit) {
      const handleEdit = (event: React.MouseEvent): void => {
        event.preventDefault();
        event.stopPropagation();
        if (post.template?.id !== undefined) {
          window.open(buildUpdateTemplateUrl(post.template.id), '_blank');
        }
      };
      actionsArray.push({
        key: 'update',
        dataTestId: `${DATA_TEST_ID_PREFIX}--update`,
        label: 'Update',
        icon: isOptionList ? CreateIcon : ElementEditIcon,
        onClick: handleEdit,
      });
    }

    const handleShare = async (): Promise<void> => {
      const link = buildPostShareUrl(post, user?.slug);
      await navigator.clipboard.writeText(link);
      fireToast({ label: 'Link copied to clipboard' });
    };

    actionsArray.push({
      key: 'copyLink',
      dataTestId: `${DATA_TEST_ID_PREFIX}--copy-link`,
      label: 'Copy Link',
      icon: isOptionList ? LinkShareIcon : LinkIcon,
      onClick: handleShare,
    });

    if (isBookmarkIconEnabled) {
      const handleBookmarkAndLike = () => {
        const isCurrentlyBookmarked = isBookmarked(post.template.id);

        toggleBookmark(post.template.id);

        if (!liked && !isCurrentlyBookmarked) {
          likePost(post.id);
          onLike?.();
        }
      };

      actionsArray.push({
        key: 'bookmark',
        dataTestId: `${DATA_TEST_ID_PREFIX}--bookmark`,
        label: isBookmarked(post.template.id) ? 'Bookmarked' : 'Bookmark',
        icon: isBookmarked(post.template.id)
          ? FilledElementBookmarkIcon
          : ElementBookmarkIcon,
        onClick: handleBookmarkAndLike,
      });
    }

    if (!isBookmarkIconEnabled && canLike) {
      const handleLikePost = (): void => {
        likePost(post.id);
      };
      actionsArray.push({
        key: 'like',
        dataTestId: `${DATA_TEST_ID_PREFIX}--like`,
        label: 'Like',
        icon: isOptionList ? (liked ? FilledHeartIcon : HeartIcon) : LikeIcon,
        onClick: handleLikePost,
        isActive: liked,
        disabled: !user || post.likeStatus === 'pending',
      });
    }

    if (onDetailsClick) {
      actionsArray.push({
        key: 'openTemplateDetails',
        dataTestId: `${DATA_TEST_ID_PREFIX}--open-template-details`,
        label: 'Template Page',
        icon: isOptionList ? InfoThickIcon : InfoIcon,
        onClick: onDetailsClick,
      });
    }

    if (onDelete) {
      const handleDelete = (event: React.MouseEvent): void => {
        event.preventDefault();
        event.stopPropagation();
        onDelete?.(post);
      };
      actionsArray.push({
        key: 'delete',
        dataTestId: `${DATA_TEST_ID_PREFIX}--delete`,
        label: 'Delete',
        icon: isOptionList ? DeleteIcon : ElementDeleteIcon,
        onClick: handleDelete,
      });
    }

    return actionsArray;
  }, [
    canEdit,
    canLike,
    isBookmarkIconEnabled,
    liked,
    onDelete,
    onDetailsClick,
    post,
    user,
    likePost,
    isBookmarked,
    toggleBookmark,
    isOptionList,
    onLike,
  ]);

  if (isOptionList) {
    return (
      <LayoutSwitch
        desktopProps={{
          placement: 'right',
          align: 'start',
        }}
        mobileProps={{
          placement: 'bottom',
          align: 'end',
        }}
      >
        <Popover
          hideTip
          content={<OptionList options={actions} />}
          showHeader={false}
          width="120px"
          onClick={(event: React.MouseEvent): void => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <LayoutSwitch
            desktopProps={{
              height: '24px',
            }}
            mobileProps={{
              height: '34px',
            }}
          >
            <IconButton
              ariaLabel="Preview"
              dataTestId="design-posts--preview-actions"
              icon={DesignElementSettingsIcon}
              name="previewButton"
              theme={circledIconTheme}
            />
          </LayoutSwitch>
        </Popover>
      </LayoutSwitch>
    );
  }

  return (
    <FlexRow gap="8px">
      {actions.map((action) => (
        <div key={action.key}>
          {/*it's a hack to stop nprogress not run the loader in app router. We will fix it later*/}
          <LayoutSwitch
            desktopProps={{
              height: '24px',
            }}
            mobileProps={{
              height: '34px',
            }}
          >
            <IconButton
              ariaLabel={action.key}
              dataTestId={action.key}
              disabled={!!action.disabled}
              eventOptions={{
                action: action.key,
              }}
              icon={action.icon}
              isActive={!!action.isActive}
              name={`${action.key}Button`}
              theme={circledIconTheme}
              onClick={action.onClick}
            />
          </LayoutSwitch>
        </div>
      ))}
    </FlexRow>
  );
};

export default withEventScope(TopActions, { module: 'postPreviewTopActions' });
